import { cva, type VariantProps } from "cva";

/**
 * Button Component Styles
 *
 * Structure: Variants → Contexts → Brands
 *
 * Variants:
 * - primary: Main action button with brand colors
 * - secondary: Secondary action with transparent background and border
 * - media: Special variant for media-related actions
 *
 * Contexts (only for primary/secondary):
 * - default: Standard background (white/light)
 * - onAccentDark: Dark accent background (brand primary colors)
 * - onAccentLight: Light accent background (brand background colors)
 *
 * Brands:
 * - ClassDojo (default): Purple color scheme
 * - Tutor: Emerald/green color scheme with distinctive borders
 * - Sparks: Blue color scheme (placeholder)
 * - Islands: Amber color scheme (placeholder)
 * - Neutral: Neutral color scheme for dark primary CTAs
 */
export const buttonStyles = cva({
  base: [
    "cursor-pointer flex items-center justify-center gap-x-4 font-ginto font-medium leading-100 transition-[background-color_transform_box-shadow] duration-220 ease-out-quad [&>svg]:fill-current",
    "tutor:transition-all tutor:duration-100 tutor:ease tutor:will-change-transform",
  ],
  variants: {
    variant: {
      primary: "border-1",
      secondary: "border-1",
      media: "border-1 backdrop-blur-button",
      accent: "",
    },
    tone: {
      red: "",
      yellow: "",
      blue: "",
      green: "",
      orange: "",
    },
    context: {
      default: "",
      onAccentDark: "",
      onAccentLight: "",
    },
    size: {
      xs: "px-12 text-ginto-14 rounded-xs h-32",
      s: "px-16 text-ginto-14 rounded-sm h-40",
      m: "px-24 text-ginto-16 rounded-default h-48",
      l: "px-32 text-ginto-18 rounded-md h-56",
    },
    elevation: {
      hard: "",
      none: "",
    },
    disabled: {
      true: "cursor-not-allowed",
      false: "",
    },
  },
  compoundVariants: [
    {
      variant: ["primary", "secondary"],
      elevation: "hard",
      disabled: false,
      context: ["default", "onAccentLight"],
      class:
        "tutor:shadow-button-hard-tutor tutor:active:shadow-none tutor:active:translate-x-4 tutor:active:translate-y-4",
    },
    {
      variant: ["primary", "secondary"],
      elevation: "hard",
      disabled: false,
      context: "onAccentDark",
      class:
        "tutor:shadow-button-hard-tutor-on-dark tutor:active:shadow-none tutor:active:translate-x-4 tutor:active:translate-y-4",
    },
    // Focus states for primary and secondary buttons
    {
      variant: ["primary", "secondary"],
      context: "default",
      disabled: false,
      class: "outline-border focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-border",
    },
    {
      variant: ["primary", "secondary"],
      context: "onAccentDark",
      disabled: false,
      class: "outline-border focus-visible:outline-4 focus-visible:outline-offset-0 focus-visible:outline-chalk-600",
    },
    {
      variant: ["primary", "secondary"],
      context: "onAccentLight",
      disabled: false,
      class: "outline-border focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-border",
    },
    {
      variant: "media",
      context: ["default", "onAccentDark", "onAccentLight"],
      disabled: false,
      class: "outline-border focus-visible:outline-4 focus-visible:outline-offset-0 focus-visible:outline-ink-600",
    },

    // Primary Button Styles
    // Default Context
    {
      variant: "primary",
      disabled: false,
      context: "default",
      class: [
        "bg-brand-primary text-content-light border-transparent hover:bg-brand-primary-hover active:bg-brand-primary active:scale-98",
        "tutor:bg-emerald-300 tutor:text-brand-secondary tutor:border-2 tutor:border-emerald-950 tutor:hover:bg-emerald-400 tutor:active:bg-emerald-300 tutor:active:scale-100",
        "sparks:bg-blue-700 sparks:text-content-light sparks:border-transparent sparks:hover:bg-blue-800 sparks:active:bg-blue-700",
        "islands:bg-amber-300 islands:text-content-primary islands:border-transparent islands:hover:bg-amber-400 islands:active:bg-amber-300",
        "neutral:bg-black neutral:hover:bg-neutral-800 neutral:active:bg-black",
      ],
    },
    // On Accent Dark Context
    {
      variant: "primary",
      disabled: false,
      context: "onAccentDark",
      class: [
        "bg-surface text-content-primary border-transparent hover:bg-chalk-800 active:bg-surface active:scale-98",
        "tutor:bg-surface tutor:text-content-primary tutor:border-transparent tutor:hover:bg-chalk-800 tutor:active:bg-surface tutor:active:scale-100",
        "sparks:bg-surface sparks:text-content-primary sparks:border-transparent sparks:hover:bg-chalk-800 sparks:active:bg-surface",
        "islands:bg-surface islands:text-content-primary islands:border-transparent islands:hover:bg-chalk-800 islands:active:bg-surface",
      ],
    },
    // On Accent Light Context
    {
      variant: "primary",
      disabled: false,
      context: "onAccentLight",
      class: [
        "bg-brand-primary text-content-light border-transparent hover:bg-brand-primary-hover active:bg-brand-primary active:scale-98",
        "tutor:bg-white tutor:text-brand-secondary tutor:border-2 tutor:border-emerald-950 tutor:hover:bg-chalk-800 tutor:active:bg-white tutor:active:scale-100",
        "sparks:bg-blue-700 sparks:text-content-light sparks:border-transparent sparks:hover:bg-blue-800 sparks:active:bg-blue-700",
        "islands:bg-amber-300 islands:text-content-primary islands:border-transparent islands:hover:bg-amber-400 islands:active:bg-amber-300",
      ],
    },

    // Secondary Button Styles
    // Default Context
    {
      variant: "secondary",
      context: "default",
      disabled: false,
      class: [
        "bg-transparent text-content-primary dark:text-content-light border-border-decorative dark:bg-chalk-200 dark:hover:bg-chalk-300 dark:active:bg-chalk-300 hover:bg-ink-100 dark:hover:bg-chalk-200 active:bg-transparent active:scale-98 dark:active:bg-chalk-200",
        "tutor:bg-transparent tutor:text-content-primary tutor:border-2 tutor:border-emerald-950 tutor:hover:bg-ink-100 tutor:active:bg-transparent tutor:active:scale-100",
        "sparks:bg-transparent sparks:text-content-primary sparks:border-border-decorative sparks:hover:bg-ink-100 sparks:active:bg-transparent",
        "islands:bg-transparent islands:text-content-primary islands:border-border-decorative islands:hover:bg-ink-100 islands:active:bg-transparent",
        "neutral:bg-brand-100 neutral:text-brand-950 neutral:border-brand-100 neutral:hover:border-brand-200 neutral:active:border-brand-200 neutral:hover:bg-brand-200 neutral:active:bg-brand-100",
      ],
    },
    // On Accent Dark Context
    {
      variant: "secondary",
      context: "onAccentDark",
      disabled: false,
      class: [
        "bg-transparent text-content-light border-2 border-content-light hover:bg-ink-200 active:bg-transparent active:scale-98",
        "tutor:bg-transparent tutor:text-content-light tutor:border-2 tutor:border-content-light tutor:hover:bg-ink-200 tutor:active:bg-transparent tutor:active:scale-100",
        "sparks:bg-transparent sparks:text-content-light sparks:border-2 sparks:border-content-light sparks:hover:bg-ink-200 sparks:active:bg-transparent",
        "islands:bg-transparent islands:text-content-light islands:border-2 islands:border-content-light islands:hover:bg-ink-200 islands:active:bg-transparent",
      ],
    },
    // On Accent Light Context
    {
      variant: "secondary",
      context: "onAccentLight",
      disabled: false,
      class: [
        "bg-transparent text-content-primary border-border-decorative hover:bg-ink-100 active:bg-transparent active:scale-98",
        "tutor:bg-transparent tutor:text-content-primary tutor:border-2 tutor:border-content-primary tutor:hover:bg-ink-100 tutor:active:bg-transparent tutor:active:scale-100",
        "sparks:bg-transparent sparks:text-content-primary sparks:border-border-decorative sparks:hover:bg-ink-100 sparks:active:bg-transparent",
        "islands:bg-transparent islands:text-content-primary islands:border-border-decorative islands:hover:bg-ink-100 islands:active:bg-transparent",
      ],
    },

    // Media Button Styles (no context variants)
    {
      variant: "media",
      disabled: false,
      class: "bg-ink-500 text-white border-transparent hover:bg-ink-600 active:bg-ink-500 active:scale-98",
    },

    // Accent Button Styles
    {
      variant: "accent",
      tone: "red",
      disabled: false,
      class:
        "bg-red-300 text-content-primary border-2 border-red-700 hover:bg-red-400 active:bg-red-400 active:scale-98 shadow-button-hard-red hover:shadow-button-hard-red-hover hover:-translate-y-4 active:shadow-none active:translate-y-4",
    },
    {
      variant: "accent",
      tone: "yellow",
      disabled: false,
      class:
        "bg-yellow-300 text-content-primary border-2 border-amber-700 hover:bg-yellow-400 active:bg-yellow-400 active:scale-98 shadow-button-hard-yellow hover:shadow-button-hard-yellow-hover hover:-translate-y-4 active:shadow-none active:translate-y-4",
    },
    {
      variant: "accent",
      tone: "blue",
      disabled: false,
      class:
        "bg-blue-300 text-content-primary border-2 border-blue-700 hover:bg-blue-400 active:bg-blue-400 active:scale-98 shadow-button-hard-blue hover:shadow-button-hard-blue-hover hover:-translate-y-4 active:shadow-none active:translate-y-4",
    },
    {
      variant: "accent",
      tone: "green",
      disabled: false,
      class:
        "bg-green-300 text-content-primary border-2 border-green-700 hover:bg-green-400 active:bg-green-400 active:scale-98 shadow-button-hard-green hover:shadow-button-hard-green-hover hover:-translate-y-4 active:shadow-none active:translate-y-4",
    },
    {
      variant: "accent",
      tone: "orange",
      disabled: false,
      class:
        "bg-orange-300 text-content-primary border-2 border-orange-700 hover:bg-orange-400 active:bg-orange-400 active:scale-98 shadow-button-hard-orange hover:shadow-button-hard-orange-hover hover:-translate-y-4 active:shadow-none active:translate-y-4",
    },

    // Disabled States
    {
      variant: "primary",
      disabled: true,
      class: "bg-ink-200 text-ink-400 border-transparent",
    },
    {
      variant: "secondary",
      disabled: true,
      class: "bg-ink-200 text-ink-400 border-transparent",
    },
    {
      variant: "media",
      disabled: true,
      class: "bg-ink-200 text-ink-400 border-transparent",
    },
    {
      variant: "accent",
      disabled: true,
      class: "bg-ink-200 text-ink-400 border-transparent",
    },

    // Icon Sizing
    {
      size: ["xs", "s"],
      class: "[&>svg]:w-18 [&>svg]:h-18",
    },
    {
      size: ["m", "l"],
      class: "[&>svg]:w-24 [&>svg]:h-24",
    },
  ],
});

export type ButtonStylesProps = VariantProps<typeof buttonStyles>;
